"use client"

import { useEffect } from "react"
import { useQueryClient, type QueryClient } from "@tanstack/react-query"
import { usePubSub } from "./usePubSub"
import { onPusherReconnect } from "@/lib/pusherClient"
import { createCoalescerRegistry } from "@/utils/coalesce.utils"
import { reportClientWarning } from "@/lib/clientReporting"

export const FREIGHT_DASHBOARD_SYNC_COALESCE_MS = 30_000

const DASHBOARD_QUERY_KEYS = [
    [["freightDashboard", "summary"]],
    [["freightDashboard", "activitySummary"]],
    [["freightDashboard", "carrierPerformanceSummary"]],
    [["freightDashboard", "warehouseFlow"]],
]

const dashboardInvalidators = createCoalescerRegistry<QueryClient>()

export function useFreightDashboardSync({
    coalesceMs = FREIGHT_DASHBOARD_SYNC_COALESCE_MS,
}: { coalesceMs?: number } = {}) {
    const queryClient = useQueryClient()
    const invalidator = dashboardInvalidators.forKey({
        key: queryClient,
        windowMs: coalesceMs,
        run: () => {
            try {
                for (const queryKey of DASHBOARD_QUERY_KEYS) {
                    queryClient.invalidateQueries({
                        queryKey,
                        refetchType: "active",
                    })
                }
            } catch (err) {
                reportClientWarning({
                    message: "useFreightDashboardSync: invalidation failed",
                    cause: err as Error,
                    tags: { surface: "useFreightDashboardSync" },
                })
            }
        },
    })

    useEffect(() => {
        return onPusherReconnect(() => invalidator.schedule())
    }, [queryClient, invalidator])

    usePubSub([
        {
            events: ["documentUpdated"],
            callback: event => {
                if (event.documentType !== "shipment") return
                invalidator.schedule()
            },
        },
    ])
}
